/**
 * Runtime configuration.
 *
 * Local dev: values from .env via dotenv-webpack (build-time).
 * Deployed:
 *   /config.json (public, pre-auth): Cognito IDs for sign-in
 *   /app-config.json (behind auth): Lambda name
 */

interface AppConfig {
  region: string;
  cognitoUserPoolId: string;
  cognitoClientId: string;
  cognitoIdentityPoolId: string;
  cognitoDomain: string;
  apiLambdaName: string;
  appDomain: string;
}

const defaults: AppConfig = {
  region: process.env.REGION ?? "eu-west-1",
  cognitoUserPoolId: process.env.COGNITO_USER_POOL_ID ?? "",
  cognitoClientId: process.env.COGNITO_CLIENT_ID ?? "",
  cognitoIdentityPoolId: process.env.COGNITO_IDENTITY_POOL_ID ?? "",
  cognitoDomain: process.env.COGNITO_DOMAIN ?? "",
  apiLambdaName: process.env.API_LAMBDA_NAME ?? "",
  appDomain: process.env.APP_DOMAIN ?? window?.location?.origin ?? "https://localhost:3000",
};

export let config: AppConfig = { ...defaults };

async function fetchJson(path: string): Promise<Record<string, string> | null> {
  try {
    const resp = await fetch(path);
    if (resp.ok) return resp.json();
  } catch { /* not available */ }
  return null;
}

/** Load public config (Cognito IDs). Called before auth. */
export async function loadConfig(): Promise<AppConfig> {
  const runtime = await fetchJson("/config.json");
  if (runtime) {
    config = { ...config, ...runtime, appDomain: window?.location?.origin ?? config.appDomain };
  }
  return config;
}

/** Load private config (Lambda name). Called after auth. */
export async function loadAppConfig(): Promise<AppConfig> {
  const runtime = await fetchJson("/app-config.json");
  if (runtime) {
    config = { ...config, ...runtime };
  }
  return config;
}

/** Get build version from /build-info.json. */
export async function getBuildVersion(): Promise<string> {
  const info = await fetchJson("/build-info.json");
  return info?.version || "dev";
}
